import { useEffect } from 'react';
import { option } from 'fp-ts';
import { useResolveRoute } from '@mews/routing';
import { useDialogs } from '@mews-commander/ui';
import { WithBillId } from '@mews-commander/types';
import { lt, showSuccessAlert } from '@mews/framework';
import { RouteName } from '@mews-commander/enums';
import { AccountType, Bill } from '@mews-commander/data-access';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { navigateInBaseModal } from '../../../app/actions/common';
import { FormValues, useReinstateFormControls } from '../form';
import { REINSTATE_BILL_DIALOG_ID } from '../constants';
import { useBillReinstateDetails } from '../hooks';
import { useGetAccount } from '../../../accounts';
import { CompanyDetailTabs } from '../../../companyDetail/enums';
import { useBillReinstate } from './useBillReinstate';
import { useBillsQueryInvalidation } from './useBillsQueryInvalidation';

export const useDialogControls = ({ billId }: WithBillId) => {
    const dispatch = useCommanderThunkDispatch();
    const resolveRoute = useResolveRoute();
    const { closeDialog } = useDialogs();
    const { bill, isFetching: isBillFetching } = useBillReinstateDetails({ billId });
    const billOwnerId = bill?.ownerId;
    const hasBillOwnerId = !!billOwnerId;
    const { data: account, isFetching: isAccountFetching } = useGetAccount(
        { accountId: billOwnerId || '' },
        { enabled: hasBillOwnerId }
    );
    const invalidate = useBillsQueryInvalidation();

    const onSuccess = (newBill: Bill) => {
        const documentName = bill?.name || billId;
        const newDocumentName = newBill.creditNoteTitle || newBill.name || '';

        showSuccessAlert({
            title: lt('BillReissueSuccessMessageTitle'),
            description: lt('BillReissueSuccessMessageText', {
                originalDocument: documentName,
                creditNoteName: newDocumentName,
            }),
        });

        if (hasBillOwnerId) {
            const isCompanyAccount = account ? account.data.discriminator === AccountType.Company : false;
            const url = isCompanyAccount
                ? resolveRoute(RouteName.CompanyDetail, {
                      id: billOwnerId,
                      query: { tab: CompanyDetailTabs.Billing, billId: newBill.id },
                  })
                : resolveRoute(RouteName.BillingAccount, { accountId: billOwnerId, query: { billId: newBill.id } });

            dispatch(navigateInBaseModal(url));
        }

        closeDialog(REINSTATE_BILL_DIALOG_ID);
    };

    const { mutate, isPending: isLoading, isSuccess } = useBillReinstate({ onSuccess });

    useEffect(
        () => () => {
            if (isSuccess) {
                invalidate();
            }
        },
        [invalidate, isSuccess]
    );

    const handleClose = () => {
        if (isLoading) return;

        closeDialog(REINSTATE_BILL_DIALOG_ID);
    };
    const handleSubmit = (values: FormValues) => {
        mutate({ billId, ...values });
    };

    const { submit, renderFormContent } = useReinstateFormControls({
        onSubmit: handleSubmit,
        billType: option.fromNullable(bill?.type),
    });

    return {
        bill,
        handleClose,
        isLoading: isBillFetching || isAccountFetching || isLoading,
        submit,
        renderFormContent,
    };
};
